import React, { createContext, useContext, ReactNode, useCallback, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface HeaderTheme {
    bgColor?: string;
    textColor?: string;
}

// 添加副标题导航项的类型
export interface SubnavItem {
    name: string; // 这将用作i18n翻译的键
    href: string;
    id: string;
}

interface HeaderContextType {
    theme: HeaderTheme;
    setHeaderTheme: (theme: HeaderTheme) => void;
    isScrolled: boolean;
    activeLink: string;
    activeSubLink: string;
    subNavItems: SubnavItem[];
}

const defaultTheme: HeaderTheme = {
    bgColor: 'bg-white',
    textColor: 'text-[#142b89]'
};

// 预设的副标题导航项 - 使用翻译键而非直接文本
const defaultSubNavItems: SubnavItem[] = [
    { name: 'header.subnav.home', href: '/', id: 'home' },
    { name: 'header.subnav.about', href: '/about', id: 'about' },
    { name: 'header.nav.news', href: '/news', id: 'news' },
    { name: 'header.subnav.calendar', href: '/calendar', id: 'calendar' },
    { name: 'header.subnav.projects', href: '/programs', id: 'programs' },
    { name: 'header.subnav.teachers', href: '/teachers', id: 'teachers' },
    { name: 'header.subnav.contact', href: '/contact', id: 'contact' },
    { name: 'header.subnav.donate', href: '/donate', id: 'donate' },
];

const HeaderContext = createContext<HeaderContextType | undefined>(undefined);

export const HeaderProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [theme, setTheme] = useState<HeaderTheme>(defaultTheme);
    const [isScrolled, setIsScrolled] = useState(false);
    const location = useLocation();
    const [activeLink, setActiveLink] = useState('/');
    const [activeSubLink, setActiveSubLink] = useState('');
    const [subNavItems] = useState<SubnavItem[]>(defaultSubNavItems);

    // 监听滚动事件
    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY;
            if (scrollPosition > 50) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    // 监听路由变化，更新活动链接
    useEffect(() => {
        const path = location.pathname;
        setActiveLink(path);
        
        // 更新活动的副标题链接
        const subItem = subNavItems.find(item => path === item.href);
        if (subItem) {
            setActiveSubLink(subItem.id);
        } else {
            setActiveSubLink('');
        }
    }, [location, subNavItems]);

    // 使用 useCallback 来记忆 setHeaderTheme 函数
    const setHeaderTheme = useCallback((newTheme: HeaderTheme) => {
        setTheme(newTheme);
    }, []); // 空依赖数组，因为这个函数不依赖任何外部变量

    const value = React.useMemo(() => ({
        theme,
        setHeaderTheme,
        isScrolled,
        activeLink,
        activeSubLink,
        subNavItems
    }), [theme, setHeaderTheme, isScrolled, activeLink, activeSubLink, subNavItems]);

    return (
        <HeaderContext.Provider value={value}>
            {children}
        </HeaderContext.Provider>
    );
};

export const useHeader = () => {
    const context = useContext(HeaderContext);
    if (context === undefined) {
        throw new Error('useHeader must be used within a HeaderProvider');
    }
    return context;
}; 